// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useRef, useState, type CSSProperties } from 'react';

import type { ResourceView, SurveyType } from '../../../api/types';
import type { Badge } from '../../../bygning/review';
import { TREATMENT_LABEL } from '../../../kortlaegning/vocab';
import { Pill } from '../../Pill';
import { ViewImage } from '../../kortlaegning/ViewImage';
import styles from './Galleri.module.css';

/** The image's longest drawn edge on a card (px), for the fetch size. */
const DISPLAY_SIZE = 400;
/** Fetch cap: a tight region of a large frame still stays card-sized. */
const FETCH_CAP = 768;

export interface GalleryCardProps {
  type: SurveyType;
  /** Its index in the grid (`data-index`, for the arrow keys). */
  index: number;
  selected: boolean;
  /** The grid's one tab stop (roving tabindex). */
  tabbable: boolean;
  /** The lens colour (a token name); null = the ghost swatch. */
  swatch: string | null;
  /** The resource image (`shownView`) and the part it belongs to; `view: null` = the placeholder. */
  view: ResourceView | null;
  /** The placeholder's material tint (a token name) and text. */
  placeholder: { token: string; text: string };
  description: string;
  env: Badge;
  review: Badge;
  onSelect: () => void;
  /** Double-click on a card with an image: open that part's Foto evidence. */
  onOpen?: () => void;
}

/**
 * One type in Galleri: the resource image (4:3, edge to edge), then swatch,
 * name and `#id`, the muted description, and the behandling, miljøstatus and
 * review pills. A button with `aria-pressed`: a click selects the type.
 */
export function GalleryCard({
  type,
  index,
  selected,
  tabbable,
  swatch,
  view,
  placeholder,
  description,
  env,
  review,
  onSelect,
  onOpen,
}: GalleryCardProps) {
  return (
    <button
      type="button"
      className={styles.card}
      aria-pressed={selected}
      data-type-id={type.id}
      data-index={index}
      tabIndex={tabbable ? 0 : -1}
      onClick={onSelect}
      onDoubleClick={onOpen}
    >
      <CardImage view={view} placeholder={placeholder} />
      <span className={styles.body}>
        <span className={styles.head}>
          <i
            className={swatch ? styles.swatch : `${styles.swatch} ${styles.ghost}`}
            style={swatch ? { background: `var(${swatch})` } : undefined}
            aria-hidden="true"
          />
          <span className={styles.name} title={type.name}>
            {type.starred && (
              <span className={styles.star} role="img" aria-label="Vigtig">
                ★{' '}
              </span>
            )}
            {type.name}
          </span>
          <span className={styles.id}>#{type.id}</span>
        </span>
        <span className={styles.desc} title={description}>
          {description}
        </span>
        <span className={styles.pills}>
          <Pill treatment={type.treatment}>{TREATMENT_LABEL[type.treatment]}</Pill>
          <Pill tone={env.tone}>{env.label}</Pill>
          <span className={styles.review}>
            <Pill tone={review.tone}>{review.label}</Pill>
          </span>
        </span>
      </span>
    </button>
  );
}

/**
 * The image well (type and part cards alike). The image only fetches once the
 * card scrolls near the view (a large survey is hundreds of cards, each an
 * image request) — until then the well is empty. Without a view it is the
 * material-tinted placeholder.
 */
export function CardImage({ view, placeholder }: { view: ResourceView | null; placeholder: { token: string; text: string } }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (near || view === null) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setNear(true);
      },
      { rootMargin: '300px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, view]);

  if (view === null) {
    return (
      <span className={styles.media}>
        <span className={styles.placeholder} style={{ '--tint': `var(${placeholder.token})` } as CSSProperties}>
          {placeholder.text}
        </span>
      </span>
    );
  }
  return (
    <span ref={ref} className={styles.media} aria-hidden="true">
      {near && <ViewImage view={view} displaySize={DISPLAY_SIZE} maxFetch={FETCH_CAP} fit="cover" className={styles.image} label={null} quietFallback />}
    </span>
  );
}
